/**
 * O token vive AQUI, e so aqui: numa variavel de modulo.
 *
 * docs/AUTENTICACAO-E-ACESSO.md, secao 4: "O token fica em memoria na
 * aplicacao, nunca em armazenamento local." Recarregar a pagina apaga esta
 * variavel de proposito — quem devolve a sessao e a autenticacao silenciosa
 * contra o cookie de identidade do Keycloak (ver fluxo-autorizacao.ts), nao um
 * token guardado no navegador.
 *
 * Consequencia aceita: extensao de navegador, XSS persistido ou aba
 * inspecionada nao encontram credencial em `localStorage`/`sessionStorage`
 * porque nunca houve credencial la. O teste armazem-token.spec.ts falha se
 * alguem, um dia, decidir "so guardar para nao perder no F5".
 */

export interface SessaoToken {
  /** Token de acesso, enviado a API no cabecalho Authorization. */
  acesso: string;
  /** Token de renovacao, usado na renovacao silenciosa. Tambem so em memoria. */
  renovacao: string | null;
  /** Token de identidade, exigido pelo Keycloak como pista na saida. */
  identidade: string | null;
  /** Instante de expiracao do token de acesso, em milissegundos da epoca. */
  expiraEm: number;
}

type Ouvinte = (sessao: SessaoToken | null) => void;

let sessaoAtual: SessaoToken | null = null;
const ouvintes = new Set<Ouvinte>();

/** Margem de seguranca: o token e tratado como vencido um pouco antes da hora. */
export const MARGEM_RENOVACAO_MS = 60_000;

export function definirSessao(sessao: SessaoToken): void {
  sessaoAtual = sessao;
  notificar();
}

export function limparSessao(): void {
  sessaoAtual = null;
  notificar();
}

export function sessaoAtiva(): SessaoToken | null {
  return sessaoAtual;
}

/** Verdadeiro quando ha token e ele ainda vale, descontada a margem. */
export function tokenUtilizavel(agora = Date.now()): boolean {
  return sessaoAtual !== null && sessaoAtual.expiraEm - MARGEM_RENOVACAO_MS > agora;
}

/** Milissegundos que faltam para a proxima renovacao. Nunca negativo. */
export function msAteRenovar(agora = Date.now()): number {
  if (!sessaoAtual) {
    return 0;
  }
  return Math.max(0, sessaoAtual.expiraEm - MARGEM_RENOVACAO_MS - agora);
}

/** Assina mudancas de sessao. Devolve a funcao que cancela a assinatura. */
export function assinarSessao(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

function notificar(): void {
  for (const ouvinte of ouvintes) {
    ouvinte(sessaoAtual);
  }
}

/**
 * Monta a sessao a partir da resposta do ponto de token do Keycloak.
 *
 * `expires_in` vem em segundos e e relativo ao instante da resposta; guardar o
 * instante absoluto evita recalcular com relogio que andou.
 */
export function sessaoDaResposta(
  resposta: {
    access_token: string;
    refresh_token?: string;
    id_token?: string;
    expires_in?: number;
  },
  agora = Date.now(),
): SessaoToken {
  const segundos = typeof resposta.expires_in === 'number' ? resposta.expires_in : 0;
  return {
    acesso: resposta.access_token,
    renovacao: resposta.refresh_token ?? null,
    identidade: resposta.id_token ?? null,
    expiraEm: agora + segundos * 1000,
  };
}
